/**
 * Access policy — narrow analytics grants on top of the org role (E1.5, #1336).
 * Spec: docs/plans/2026-10-01-dashboard-e15-access-grants.md, «Решения».
 *
 * Three modes, and only one of them consults the role:
 *
 * - `legacy` — no active grant. The role decides exactly as before; every
 *   capability check passes so that no existing subject loses a byte.
 * - `managed` — an active grant. Only the grant's capability snapshot counts:
 *   a missing capability beats the role, own/hierarchy visibility and project
 *   membership.
 * - `unavailable` — the policy could not be read, or the stored grant does not
 *   parse. Never folded into `legacy`: a reader that cannot tell whether the
 *   subject is narrowed must deny sensitive data (spec item 6, A02).
 *
 * Pure: no I/O. `@aica/api-types` is imported for TYPES only — a value import
 * from core reorders an import cycle and breaks unrelated edge tests — so the
 * vocabulary is re-validated here by hand against `ANALYTICS_CAPABILITY_VALUES`,
 * which the type checker ties to the api-types enum in both directions.
 */
import type { AccessPreset, AccessScope, AnalyticsCapability } from '@aica/api-types';

/** Every capability, in the api-types enum order. */
const ANALYTICS_CAPABILITY_VALUES = [
  'aggregates.flow',
  'aggregates.sales',
  'aggregates.quality',
  'aggregates.team',
  'aggregates.voc',
  'team.scores',
  'records.read',
  'evidence.text',
  'evidence.audio',
] as const satisfies readonly AnalyticsCapability[];

// The list above must name every member of the api-types enum: a capability
// added there but not here would be silently dropped from every grant.
type MissingCapability = Exclude<
  AnalyticsCapability,
  (typeof ANALYTICS_CAPABILITY_VALUES)[number]
>;
const capabilityListIsComplete: [MissingCapability] extends [never] ? true : false = true;
void capabilityListIsComplete;

const KNOWN_CAPABILITIES: ReadonlySet<string> = new Set(ANALYTICS_CAPABILITY_VALUES);

const AGGREGATES = [
  'aggregates.flow',
  'aggregates.sales',
  'aggregates.quality',
  'aggregates.team',
  'aggregates.voc',
] as const satisfies readonly AnalyticsCapability[];

/**
 * What each preset grants at the moment of assignment. The grant stores a
 * snapshot of this list; changing it here does not touch grants already made.
 */
export const PRESET_CAPABILITIES: Readonly<
  Record<AccessPreset, readonly AnalyticsCapability[]>
> = {
  // Section aggregates only: no records, quotes, audio or employee scores.
  analyst: [...AGGREGATES],
  // Everything about the group — within its scope only.
  group_lead: [
    ...AGGREGATES,
    'team.scores',
    'records.read',
    'evidence.text',
    'evidence.audio',
  ],
};

export type AccessPolicy =
  | { mode: 'legacy'; revision: number }
  | {
      mode: 'managed';
      preset: AccessPreset;
      capabilities: ReadonlySet<AnalyticsCapability>;
      scope: AccessScope;
      revision: number;
    }
  | { mode: 'unavailable' };

/**
 * The stored grant as it comes out of `access_grants`: the JSON columns are
 * still text, so every way a row can be malformed is decided here, once.
 */
export interface StoredAccessGrant {
  preset: string;
  capabilitiesJson: string;
  scopeJson: string;
}

export const UNAVAILABLE_ACCESS_POLICY: AccessPolicy = Object.freeze({
  mode: 'unavailable',
});

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function isAccessPreset(value: unknown): value is AccessPreset {
  return value === 'analyst' || value === 'group_lead';
}

/**
 * Mirrors `ACCESS_SCOPE_MAX_IDS` / `ACCESS_PROJECTS_SCOPE_MAX_IDS` /
 * `ACCESS_SCOPE_ID_MAX_LENGTH` in api-types (a literal: core may import
 * api-types for types only; the test ties them). The projects bound is the
 * project filter's (`PROJECT_IDS_MAX`) — the reason is in api-types.
 */
const SCOPE_MAX_IDS = 200;
const PROJECTS_SCOPE_MAX_IDS = 40;
const SCOPE_ID_MAX_LENGTH = 64;

/**
 * Which scopes each preset may carry — mirrors `PRESET_SCOPE_KINDS` in
 * api-types (type-tied below). Analyst: whole org or projects; group lead: a
 * department subtree only.
 */
const PRESET_SCOPE_KINDS = {
  analyst: ['org', 'projects'],
  group_lead: ['departments'],
} as const satisfies Record<AccessPreset, readonly AccessScope['kind'][]>;

/** Mirrors `scopeIdsSchema`: 1..max ids of 1..64 chars, duplicates collapsed. */
function parseIds(value: unknown, max: number): string[] | null {
  if (!Array.isArray(value) || value.length === 0 || value.length > max) {
    return null;
  }
  if (
    !value.every(
      (id) => typeof id === 'string' && id.length > 0 && id.length <= SCOPE_ID_MAX_LENGTH
    )
  ) {
    return null;
  }
  return [...new Set(value as string[])];
}

/** Mirrors `accessScopeSchema`; null when the stored scope does not parse. */
function parseScope(value: unknown): AccessScope | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const { kind, ids } = value as { kind?: unknown; ids?: unknown };
  if (kind === 'org') return { kind: 'org' };
  if (kind === 'projects' || kind === 'departments') {
    const parsed = parseIds(
      ids,
      kind === 'projects' ? PROJECTS_SCOPE_MAX_IDS : SCOPE_MAX_IDS
    );
    return parsed ? { kind, ids: parsed } : null;
  }
  return null;
}

/**
 * Policy for one subject in one org.
 *
 * - no active grant → `legacy`;
 * - a grant → `managed`. Its capabilities are the stored snapshot NARROWED by
 *   the preset's current list (`PRESET_CAPABILITIES`): narrowing a preset in
 *   code takes effect on grants already given, widening it never does. A
 *   capability string outside the vocabulary is dropped (default deny);
 * - a grant whose preset, capability list or scope does not parse, or whose
 *   scope is not one its preset may carry (an analyst over departments, a group
 *   lead over the whole org) → `unavailable`, never `legacy`: the row says the
 *   subject is narrowed, and falling back to the role would hand back
 *   everything the grant took away.
 */
export function policyFromGrant(
  grant: StoredAccessGrant | null,
  revision: number
): AccessPolicy {
  if (grant === null) return { mode: 'legacy', revision };

  if (!isAccessPreset(grant.preset)) return UNAVAILABLE_ACCESS_POLICY;

  const scope = parseScope(parseJson(grant.scopeJson));
  if (!scope) return UNAVAILABLE_ACCESS_POLICY;
  const allowedKinds: readonly AccessScope['kind'][] = PRESET_SCOPE_KINDS[grant.preset];
  if (!allowedKinds.includes(scope.kind)) return UNAVAILABLE_ACCESS_POLICY;

  const stored = parseJson(grant.capabilitiesJson);
  if (!Array.isArray(stored) || !stored.every((cap) => typeof cap === 'string')) {
    return UNAVAILABLE_ACCESS_POLICY;
  }
  const presetCapabilities: ReadonlySet<string> = new Set(
    PRESET_CAPABILITIES[grant.preset]
  );
  const capabilities = new Set<AnalyticsCapability>(
    (stored as string[]).filter(
      (cap): cap is AnalyticsCapability =>
        KNOWN_CAPABILITIES.has(cap) && presetCapabilities.has(cap)
    )
  );

  return { mode: 'managed', preset: grant.preset, capabilities, scope, revision };
}

/**
 * Does the policy allow this capability?
 *
 * `legacy` → true: the role still decides, unchanged. `managed` → membership
 * in the snapshot. `unavailable` → false.
 */
export function hasCapability(
  policy: AccessPolicy,
  capability: AnalyticsCapability
): boolean {
  switch (policy.mode) {
    case 'legacy':
      return true;
    case 'managed':
      return policy.capabilities.has(capability);
    case 'unavailable':
      return false;
  }
}

/**
 * What the rendered parts of a notification about a call or a person need —
 * the participant, the model's reason, the employee, the score, the link into
 * the record (E1.5, #1644). Which of them a template used is not knowable from
 * the stored row, so a managed subject gets them only with EVERY capability
 * here. One list for the inbox (edge-api `projectNotificationList`) and for
 * delivery (`narrowNotificationAudience`, `@aica/database`), so what a grant
 * holder reads in-app and what reaches its mailbox cannot differ.
 */
export const NOTIFICATION_CONTENT_CAPABILITIES = [
  'records.read',
  'evidence.text',
  'team.scores',
] as const satisfies readonly AnalyticsCapability[];

/**
 * Whether a notification about a call may carry its rendered content to this
 * subject: `legacy` — yes, the role decides as before; `managed` — only with
 * every {@link NOTIFICATION_CONTENT_CAPABILITIES}; `unavailable` — no.
 */
export function notificationContentAllowed(policy: AccessPolicy): boolean {
  return NOTIFICATION_CONTENT_CAPABILITIES.every((capability) =>
    hasCapability(policy, capability)
  );
}
